// Il corpo di un tab raggruppato: più sezioni impilate sotto la stessa etichetta.
// Usato dalle schede autista, mezzo e azienda.
//
// Autisti e mezzi avevano tredici tab a testa. `TabsContainer` divide la riga in
// parti uguali, quindi con tredici voci ogni etichetta finiva in "…" e restava
// solo l'icona: la barra non traboccava, si schiacciava. I tab ora raggruppano
// più schede per tema e questo componente ne rende i corpi uno sotto l'altro.
//
// Le sotto-schede conservano il **proprio** percorso (`/fleet/vehicles/:id/costs`
// resta valido): arrivandoci in deep link si apre il gruppo e si scorre alla
// sezione, che è il ruolo di `activeSection`.
import React, { useEffect, useRef } from 'react';

import { faClass } from '@/constants/icons';
import i18n from '@/i18n';

import styles from './DetailTabSections.module.scss';

/**
 * @param sections   sezioni già filtrate per ruolo (`{ key, label, icon, Component }`)
 * @param activeSection chiave della sezione da mostrare all'apertura
 * @param renderSection (section) => nodo — il corpo, montato dal chiamante che
 *                      conosce le prop del dominio (`row`, `embedded`, …)
 */
export default function DetailTabSections({ sections, activeSection, renderSection }) {
  const refs = useRef({});
  const single = sections.length <= 1;

  // Un solo blocco non ha niente a cui scorrere, e scrollare al primo di una
  // pila sposterebbe la pagina per nulla ogni volta che si apre il gruppo.
  // Dipende dalla prima chiave e non dall'array: i chiamanti filtrano le sezioni
  // a ogni render, e un array nuovo rilanciava lo scroll a ogni aggiornamento.
  const firstKey = sections[0]?.key;
  useEffect(() => {
    if (single || !activeSection) return;
    if (firstKey === activeSection) return;
    refs.current[activeSection]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [activeSection, single, firstKey]);

  return (
    <div className={styles.sections}>
      {sections.map((s) => (
        <section
          key={s.key}
          id={`section-${s.key}`}
          ref={(el) => {
            refs.current[s.key] = el;
          }}
          className={styles.section}
          aria-labelledby={single ? undefined : `section-title-${s.key}`}
        >
          {!single && (
            <header className={styles.header}>
              {s.icon && <i className={faClass(s.icon)} aria-hidden="true" />}
              <h2 id={`section-title-${s.key}`} className={styles.title}>
                {i18n.t(s.label)}
              </h2>
            </header>
          )}
          {renderSection(s)}
        </section>
      ))}
    </div>
  );
}
